import { Injectable, inject } from '@angular/core';
import {
  AirWaterHeatPumpRequestModel,
  AtticsInsulationRequestModel,
  AtticsTypeEnum,
  BoilerTypeEnum,
  CombinedSolarSystemRequestModel,
  CostEstimatorApiPayload,
  CostEstimatorKeys,
  CostEstimatorService,
  DoubleGlazingRequestModel,
  EstimateApi,
  GeothermalHeatPumpRequestModel,
  HousingAgeEnum,
  OwnerStatusEnum,
  RoofTerraceInsulationRequestModel,
  StoveTypeEnum,
  ThermodynamicWaterHeaterRequestModel,
  WallInsulationRequestModel,
  WallInsulationTypeEnum,
  WindowMaterialEnum,
  WoodPelletBoilerRequestModel,
  WoodStoveRequestModel,
} from '@effy-tech/angular-common/cost-estimator';
import { PrecarityLevel } from '@effy-tech/angular-common/precarity-level';
import { Observable, map } from 'rxjs';
import { WORKS_SPECIFIC_ESTIMATION_LABEL } from '../../configs/works.config';
import { LocationValues } from '../../values/location.values';
import { WORK_VALUES, WorksValues } from '../../values/works.values';
import { AmountService } from '../amount/amount.service';

@Injectable({
  providedIn: 'root',
})
export class CostEstimatorHelperService {
  private readonly amountService = inject(AmountService);
  private readonly costEstimatorService = inject(CostEstimatorService);

  getEstimate$(workType: WorksValues, precarityLevel: PrecarityLevel, location: LocationValues): Observable<string> {
    const URL_BY_WORK_MAPPER: { [key in WorksValues]: (args: CostEstimatorApiPayload) => Observable<EstimateApi> } = {
      attic_insulation: (args: AtticsInsulationRequestModel) => this.costEstimatorService.estimateAtticsInsulation(args),
      air_water_heat_pump: (args: AirWaterHeatPumpRequestModel) => this.costEstimatorService.estimateAirWaterHeatPump(args),
      combined_solar_system: (args: CombinedSolarSystemRequestModel) => this.costEstimatorService.estimateCombinedSolarSystem(args),
      double_glazing: (args: DoubleGlazingRequestModel) => this.costEstimatorService.estimateDoubleGlazing(args),
      geothermal_heat_pump: (args: GeothermalHeatPumpRequestModel) => this.costEstimatorService.estimateGeothermalHeatPump(args),
      pellet_boiler: (args: WoodPelletBoilerRequestModel) => this.costEstimatorService.estimateWoodBoiler(args),
      pellet_stove: (args: WoodStoveRequestModel) => this.costEstimatorService.estimateWoodStove(args),
      roof_terrace: (args: RoofTerraceInsulationRequestModel) => this.costEstimatorService.estimateRoofTerraceInsulation(args),
      thermodynamic_water_heater: (args: ThermodynamicWaterHeaterRequestModel) =>
        this.costEstimatorService.estimateThermodynamicWaterHeater(args),
      wall_insulation: (args: WallInsulationRequestModel) => this.costEstimatorService.estimateWallInsulation(args),
      wood_boiler: (args: WoodPelletBoilerRequestModel) => this.costEstimatorService.estimateWoodBoiler(args),
      wood_pellet_boiler: (args: WoodPelletBoilerRequestModel) => this.costEstimatorService.estimateWoodBoiler(args),
      wood_stove: (args: WoodStoveRequestModel) => this.costEstimatorService.estimateWoodStove(args),
    };

    return URL_BY_WORK_MAPPER[workType](this.computeEstimateBody(precarityLevel, workType, location)).pipe(
      map(res => WORKS_SPECIFIC_ESTIMATION_LABEL(this.amountService.formatAmount(res.offre_liberte.premiums.renov), workType))
    );
  }

  private computeEstimateBody(precarityLevel: PrecarityLevel, workType: WorksValues, location: LocationValues): CostEstimatorApiPayload {
    let specificWorkBody: Partial<CostEstimatorApiPayload> = {};

    switch (workType) {
      case WORK_VALUES.AtticInsulation:
        specificWorkBody = { [CostEstimatorKeys.AtticsType]: AtticsTypeEnum.Outdoor, [CostEstimatorKeys.InsulatingSurface]: 1 };
        break;
      case WORK_VALUES.AirWaterHeatPump:
        specificWorkBody = { [CostEstimatorKeys.WithEcs]: true };
        break;
      case WORK_VALUES.DoubleGlazing:
        specificWorkBody = {
          [CostEstimatorKeys.WindowMaterial]: WindowMaterialEnum.Mixed,
          [CostEstimatorKeys.WindowsCount]: 1,
          [CostEstimatorKeys.ExistingGlazingMaterial]: true,
        };
        break;
      case WORK_VALUES.PelletBoiler:
        specificWorkBody = { [CostEstimatorKeys.BoilerType]: BoilerTypeEnum.Pellet };
        break;
      case WORK_VALUES.PelletStove:
        specificWorkBody = { [CostEstimatorKeys.StoveType]: StoveTypeEnum.Pellet };
        break;
      case WORK_VALUES.RoofTerrace:
        specificWorkBody = { [CostEstimatorKeys.InsulatingSurface]: 1 };
        break;
      case WORK_VALUES.WallInsulation:
        specificWorkBody = {
          [CostEstimatorKeys.WallInsulationType]: WallInsulationTypeEnum.Coated,
          [CostEstimatorKeys.InsulatingSurface]: 1,
        };
        break;
      case WORK_VALUES.WoodBoiler:
        specificWorkBody = { [CostEstimatorKeys.BoilerType]: BoilerTypeEnum.Wood };
        break;
      case WORK_VALUES.WoodPelletBoiler:
        specificWorkBody = { [CostEstimatorKeys.BoilerType]: BoilerTypeEnum.Wood };
        break;
      case WORK_VALUES.WoodStove:
        specificWorkBody = { [CostEstimatorKeys.StoveType]: StoveTypeEnum.Wood };
        break;
    }

    return {
      zip_code: location === LocationValues.IleDeFrance ? '75015' : '61000',
      precarity_level: precarityLevel,
      owner_status: OwnerStatusEnum.ResidentOwner,
      housing_age: HousingAgeEnum.MoreThan15Years,
      ...specificWorkBody,
    };
  }
}
